/**
 * goal-core.ts — the one Goal (goal_progress row) → core Goal (the 2D
 * PlanetView / stamp / visit-log model) adapter. PASSPORT-2.
 *
 * Every 2D Passport surface — Today's capsule, the setup wizard's
 * world stage, the visit log sheet — draws the same world the galaxy
 * does because all of them read the goal's stored `world_id` through
 * `worldOf()`: the world's name, its skin, its stamp. Nothing here is
 * hashed or indexed; archiving a goal never reskins another.
 *
 * Replaces planet-goal.ts.
 */
import { WORLD_SKIN, worldOf } from '@/components/galaxy3d/worlds';
import { goalDone, ringProgress, type Goal as PassportGoal } from '@/services/passport';
import type { Goal, PlanetState, SubGoal } from './galaxy-core';

/**
 * Translate a goal → core Goal. Each ring is one SubGoal (id = the
 * ring's id, so a tick can be addressed back); a finished goal is
 * `done`, a goal whose every ring is closed but not yet stamped is
 * `reachable` (the arrival is pending), everything else is `active`.
 */
export function goalToCore(goal: PassportGoal): Goal {
  const world = worldOf(goal);
  const subGoals: SubGoal[] = goal.rings.map((r) => ({
    id: r.id,
    title: r.label,
    type: r.ink,
    progress: goalDone(goal) ? 1 : ringProgress(r),
  }));
  const state: PlanetState = (() => {
    if (goal.archived_at) return 'locked';
    if (goal.stamped_at) return 'done';
    if (goal.completed_at) return 'reachable';
    if (subGoals.length > 0 && subGoals.every((s) => s.progress >= 1)) return 'reachable';
    return 'active';
  })();
  return {
    id: goal.id,
    title: goal.title,
    worldName: world.name,
    skin: WORLD_SKIN[world.type],
    state,
    subGoals,
    stampedAt: goal.stamped_at ?? goal.completed_at ?? undefined,
  };
}
